import { useLocation } from '@remix-run/react'
import {
  APIProvider,
  Map,
  useMap,
  type MapProps,
} from '@vis.gl/react-google-maps'
import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type ComponentProps,
  type Dispatch,
  type SetStateAction,
} from 'react'
import { flushSync } from 'react-dom'
import {
  aGoogleMapIsPanning,
  aGoogleMapIsWheelZooming,
  aGoogleMapIsZooming,
  aGoogleMapSimplifiedMode,
  aGoogleStreetViewVisible,
  aUser,
  useAtomValue,
  useSetAtom,
} from '~/atoms'
import { ClientOnly } from '~/components/common'
import {
  forceGoogleRasterRendering,
  getGoogleMapsApiKey,
  getGoogleMapTypeId,
  getGoogleRasterRenderingBrowserCheck,
  GOOGLE_MAP_DEFAULT_OPTIONS,
  GOOGLE_MAP_LIGHT_MAP_ID,
  GOOGLE_MAPS_API_LIBRARIES,
  isGoogleMapDebugRasterSearch,
  isGoogleRasterRenderingForced,
  type GoogleRasterRenderingBrowserCheck,
} from './googleMapConstants'
import { GoogleDeckOverlayProvider } from './GoogleDeckOverlay'
import type { GoogleMapStyle } from './googleMapTypes'

type GoogleApiProviderProps = Omit<
  ComponentProps<typeof APIProvider>,
  'apiKey' | 'children'
>

export type GoogleMapBaseProps = MapProps & {
  apiKey?: string
  apiProviderProps?: GoogleApiProviderProps
  mapStyle?: GoogleMapStyle
  showStreetView?: boolean
}

export function GoogleMapsApiProvider({
  apiKey = getGoogleMapsApiKey(),
  apiProviderProps,
  children,
  clientOnly = false,
}: {
  apiKey?: string
  apiProviderProps?: GoogleApiProviderProps
  children: React.ReactNode
  clientOnly?: boolean
}) {
  const provider = (
    <APIProvider
      apiKey={apiKey}
      libraries={GOOGLE_MAPS_API_LIBRARIES}
      {...apiProviderProps}>
      {children}
    </APIProvider>
  )

  if (clientOnly) {
    return <ClientOnly>{provider}</ClientOnly>
  }

  return provider
}

export function GoogleMapBase({
  apiKey = getGoogleMapsApiKey(),
  apiProviderProps,
  children,
  mapId = GOOGLE_MAP_LIGHT_MAP_ID,
  mapStyle = 'street',
  mapTypeId,
  showStreetView = true,
  ...mapProps
}: GoogleMapBaseProps) {
  const location = useLocation()
  const user = useAtomValue(aUser)
  const setSimplifiedMode = useSetAtom(aGoogleMapSimplifiedMode)
  const [forcedRasterFallback, setForcedRasterFallback] = useState(
    isGoogleRasterRenderingForced
  )
  const resolvedMapTypeId = mapTypeId ?? getGoogleMapTypeId(mapStyle)
  const debugRasterMapEnabled = isGoogleMapDebugRasterSearch(location.search)
  const browserRasterCheck = useMemo(getGoogleRasterRenderingBrowserCheck, [])
  const rasterMapEnabled =
    debugRasterMapEnabled ||
    browserRasterCheck.shouldUseRaster ||
    forcedRasterFallback
  const renderingModeReason = forcedRasterFallback
    ? 'google-renderingtype-fallback'
    : debugRasterMapEnabled
      ? 'debug-raster-param'
      : browserRasterCheck.shouldUseRaster
        ? 'browser-webgl-check'
        : 'vector-default'

  useEffect(() => {
    setSimplifiedMode(rasterMapEnabled)

    return () => {
      setSimplifiedMode(false)
    }
  }, [rasterMapEnabled, setSimplifiedMode])

  return (
    <GoogleMapsApiProvider
      apiKey={apiKey}
      apiProviderProps={apiProviderProps}
      clientOnly>
      <Map
        key={rasterMapEnabled ? `raster-${renderingModeReason}` : 'vector'}
        mapId={mapId}
        mapTypeId={resolvedMapTypeId}
        {...GOOGLE_MAP_DEFAULT_OPTIONS}
        {...mapProps}
        streetViewControl={!!user && showStreetView !== false ? true : false}
        {...(rasterMapEnabled
          ? {
              isFractionalZoomEnabled: false,
              renderingType: 'RASTER' as const,
            }
          : {})}>
        <GooglePlacePopupBlocker />
        <GoogleRenderingTypeFallbackBridge
          browserRasterCheck={browserRasterCheck}
          debugRasterMapEnabled={debugRasterMapEnabled}
          rasterMapEnabled={rasterMapEnabled}
          renderingModeReason={renderingModeReason}
          setForcedRasterFallback={setForcedRasterFallback}
        />
        <GoogleZoomStateBridge rasterMapEnabled={rasterMapEnabled} />
        <GoogleStreetViewVisibilityBridge />
        <GoogleDeckOverlayProvider>{children}</GoogleDeckOverlayProvider>
      </Map>
    </GoogleMapsApiProvider>
  )
}

type GoogleRenderingTypeFallbackBridgeProps = {
  browserRasterCheck: GoogleRasterRenderingBrowserCheck
  debugRasterMapEnabled: boolean
  rasterMapEnabled: boolean
  renderingModeReason: string
  setForcedRasterFallback: Dispatch<SetStateAction<boolean>>
}

function GoogleRenderingTypeFallbackBridge({
  browserRasterCheck,
  debugRasterMapEnabled,
  rasterMapEnabled,
  renderingModeReason,
  setForcedRasterFallback,
}: GoogleRenderingTypeFallbackBridgeProps) {
  const map = useMap()

  useEffect(() => {
    if (!map) return

    const inspectRenderingType = (source: string) => {
      const renderingType = map.getRenderingType()
      const capabilities = map.getMapCapabilities()
      const diagnostic = {
        browserRasterCheck,
        capabilities,
        rasterMapEnabled,
        renderingModeReason,
        renderingType,
        source,
      }

      if (debugRasterMapEnabled) {
        console.info('[google-map-rendering]', diagnostic)
      }

      if (
        !rasterMapEnabled &&
        renderingType === google.maps.RenderingType.RASTER
      ) {
        if (debugRasterMapEnabled) {
          console.warn(
            '[google-map-rendering] forcing raster remount',
            diagnostic
          )
        }
        forceGoogleRasterRendering()
        setForcedRasterFallback(true)
      }
    }

    const listener = map.addListener('renderingtype_changed', () => {
      inspectRenderingType('renderingtype_changed')
    })

    inspectRenderingType('mounted')

    return () => {
      listener.remove()
    }
  }, [
    browserRasterCheck,
    debugRasterMapEnabled,
    map,
    rasterMapEnabled,
    renderingModeReason,
    setForcedRasterFallback,
  ])

  return null
}

type GoogleZoomStateBridgeProps = {
  rasterMapEnabled: boolean
}

const WHEEL_ZOOM_FALLBACK_CLEAR_MS = 350
const WHEEL_ZOOM_SAFE_RESTORE_DELAY_MS = 120

function GoogleZoomStateBridge({
  rasterMapEnabled,
}: GoogleZoomStateBridgeProps) {
  const map = useMap()
  const setIsMapPanning = useSetAtom(aGoogleMapIsPanning)
  const setIsMapWheelZooming = useSetAtom(aGoogleMapIsWheelZooming)
  const setIsMapZooming = useSetAtom(aGoogleMapIsZooming)
  const wheelZoomClearTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  )
  const wheelZoomSafeRestoreTimerRef = useRef<ReturnType<
    typeof setTimeout
  > | null>(null)
  const pointerIsOverMapRef = useRef(false)

  useEffect(() => {
    if (!map) return

    const clearWheelZoomTimer = () => {
      if (!wheelZoomClearTimerRef.current) return

      clearTimeout(wheelZoomClearTimerRef.current)
      wheelZoomClearTimerRef.current = null
    }

    const clearWheelZoomSafeRestoreTimer = () => {
      if (!wheelZoomSafeRestoreTimerRef.current) return

      clearTimeout(wheelZoomSafeRestoreTimerRef.current)
      wheelZoomSafeRestoreTimerRef.current = null
    }

    const markWheelZooming = (event?: Event) => {
      if (!rasterMapEnabled) return

      const target = event?.target
      const eventStartedInMap =
        target instanceof Node ? mapDiv.contains(target) : false

      if (event && !eventStartedInMap && !pointerIsOverMapRef.current) {
        return
      }

      flushSync(() => {
        setIsMapZooming(true)
        setIsMapWheelZooming(true)
      })
      clearWheelZoomSafeRestoreTimer()
      clearWheelZoomTimer()
      wheelZoomClearTimerRef.current = setTimeout(() => {
        flushSync(() => {
          setIsMapZooming(false)
        })
        wheelZoomClearTimerRef.current = null
      }, WHEEL_ZOOM_FALLBACK_CLEAR_MS)
    }

    const mapDiv = map.getDiv()
    const handlePointerEnter = () => {
      pointerIsOverMapRef.current = true
    }
    const handlePointerLeave = () => {
      pointerIsOverMapRef.current = false
    }
    const dragStartListener = map.addListener('dragstart', () => {
      if (rasterMapEnabled) {
        setIsMapPanning(true)
      }
    })
    const zoomListener = map.addListener('zoom_changed', () => {
      if (rasterMapEnabled) {
        setIsMapZooming(true)
      }
    })
    const idleListener = map.addListener('idle', () => {
      clearWheelZoomTimer()
      flushSync(() => {
        setIsMapPanning(false)
        setIsMapZooming(false)
      })
      clearWheelZoomSafeRestoreTimer()
      wheelZoomSafeRestoreTimerRef.current = setTimeout(() => {
        flushSync(() => {
          setIsMapWheelZooming(false)
        })
        wheelZoomSafeRestoreTimerRef.current = null
      }, WHEEL_ZOOM_SAFE_RESTORE_DELAY_MS)
    })

    mapDiv.addEventListener('wheel', markWheelZooming, {
      capture: true,
      passive: true,
    })
    mapDiv.addEventListener('mouseenter', handlePointerEnter)
    mapDiv.addEventListener('mouseleave', handlePointerLeave)
    window.addEventListener('wheel', markWheelZooming, {
      capture: true,
      passive: true,
    })
    window.addEventListener('mousewheel', markWheelZooming, {
      capture: true,
      passive: true,
    })

    return () => {
      clearWheelZoomTimer()
      clearWheelZoomSafeRestoreTimer()
      mapDiv.removeEventListener('wheel', markWheelZooming, {
        capture: true,
      })
      mapDiv.removeEventListener('mouseenter', handlePointerEnter)
      mapDiv.removeEventListener('mouseleave', handlePointerLeave)
      window.removeEventListener('wheel', markWheelZooming, {
        capture: true,
      })
      window.removeEventListener('mousewheel', markWheelZooming, {
        capture: true,
      })
      pointerIsOverMapRef.current = false
      dragStartListener.remove()
      zoomListener.remove()
      idleListener.remove()
      flushSync(() => {
        setIsMapPanning(false)
        setIsMapWheelZooming(false)
        setIsMapZooming(false)
      })
    }
  }, [
    map,
    rasterMapEnabled,
    setIsMapPanning,
    setIsMapWheelZooming,
    setIsMapZooming,
  ])

  return null
}

function GoogleStreetViewVisibilityBridge() {
  const map = useMap()
  const setStreetViewVisible = useSetAtom(aGoogleStreetViewVisible)

  useEffect(() => {
    if (!map) return

    const panorama = map.getStreetView()
    const syncStreetViewVisibility = () => {
      setStreetViewVisible(panorama.getVisible())
    }
    const listener = panorama.addListener(
      'visible_changed',
      syncStreetViewVisibility
    )

    syncStreetViewVisibility()

    return () => {
      listener.remove()
      setStreetViewVisible(false)
    }
  }, [map, setStreetViewVisible])

  return null
}

function GooglePlacePopupBlocker() {
  const map = useMap()

  useEffect(() => {
    if (!map) return

    const listener = map.addListener(
      'click',
      (event: google.maps.MapMouseEvent | google.maps.IconMouseEvent) => {
        if ('placeId' in event && event.placeId) {
          event.stop()
        }
      }
    )

    return () => {
      listener.remove()
    }
  }, [map])

  return null
}
